import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, PASSWORD_RESET_VERIFIED_EVENT, ACCOUNT_VERIFIED_EVENT } from '../analytics/posthog';
import styles from './Verify.module.css';
import BrandLogo from './BrandLogo';
import config, { logger } from '../config';
import { getApiHeaders } from '../utils/api';

const Verify = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email] = useState(searchParams.get('email') || 'Rinky@richeal.com');
  const [verificationType] = useState(searchParams.get('type') || 'signup'); // 'signup' or 'forgot-password'
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendMessageType, setResendMessageType] = useState(''); // 'success' or 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationMessageType, setVerificationMessageType] = useState(''); // 'success' or 'error'
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load countdown from localStorage on component mount
  useEffect(() => {
    const savedCountdown = localStorage.getItem(config.STORAGE_KEYS.RESEND_COUNTDOWN);
    const savedTimestamp = localStorage.getItem(config.STORAGE_KEYS.RESEND_TIMESTAMP);

    if (savedCountdown && savedTimestamp) {
      const now = Date.now();
      const elapsed = Math.floor((now - parseInt(savedTimestamp)) / 1000);
      const remaining = Math.max(0, parseInt(savedCountdown) - elapsed);

      if (remaining > 0) {
        setCountdown(remaining);
        startCountdown(remaining);
      } else {
        // Clear expired countdown
        localStorage.removeItem(config.STORAGE_KEYS.RESEND_COUNTDOWN);
        localStorage.removeItem(config.STORAGE_KEYS.RESEND_TIMESTAMP);
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        const newCountdown = countdown - 1;
        setCountdown(newCountdown);

        // Update localStorage with new countdown value
        if (newCountdown > 0) {
          localStorage.setItem(config.STORAGE_KEYS.RESEND_COUNTDOWN, newCountdown.toString());
        } else {
          // Clear localStorage when countdown reaches 0
          localStorage.removeItem(config.STORAGE_KEYS.RESEND_COUNTDOWN);
          localStorage.removeItem(config.STORAGE_KEYS.RESEND_TIMESTAMP);
        }
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const startCountdown = (initialCountdown) => {
    let newCountdown = initialCountdown;

    const timer = setInterval(() => {
      newCountdown -= 1;
      setCountdown(newCountdown);

      if (newCountdown <= 0) {
        clearInterval(timer);
        // Clear localStorage when countdown reaches 0
        localStorage.removeItem(config.STORAGE_KEYS.RESEND_COUNTDOWN);
        localStorage.removeItem(config.STORAGE_KEYS.RESEND_TIMESTAMP);
      }
    }, 1000);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length === 6) {
      setIsSubmitting(true);
      setVerificationMessage('');
      setVerificationMessageType('');
      // Clear resend message when submitting verification
      setResendMessage('');
      setResendMessageType('');

      try {
        // Use different API endpoint based on verification type
        const endpoint = verificationType === 'forgot-password'
          ? config.API_ENDPOINTS.VERIFY_FORGOT_PASSWORD
          : config.API_ENDPOINTS.VERIFY_ACCOUNT;

        const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: getApiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            email: email,
            verification_code: otpString
          })
        });

        const data = await response.json();

        if (data.success) {
          if (verificationType === 'forgot-password') {
            capture(posthog, PASSWORD_RESET_VERIFIED_EVENT);
            // For forgot password flow, redirect to reset password page
            setVerificationMessage('Verification successful! Redirecting to reset password...');
            setVerificationMessageType('success');
            setIsVerified(true);
            // Navigate to reset password page with email and verification code
            setTimeout(() => {
              navigate(`/reset-password?email=${encodeURIComponent(email)}&code=${otpString}`);
            }, 2000);
          } else {
            capture(posthog, ACCOUNT_VERIFIED_EVENT);
            // For signup flow, redirect to login page (existing logic)
            setVerificationMessage('Verification successful! Please do login.');
            setVerificationMessageType('success');
            setIsVerified(true);
          }
        } else {
          // Handle error response
          let errorMessage = data.message || 'Verification failed. Please try again.';

          // Handle validation errors if they exist
          if (data.validation_errors && Array.isArray(data.validation_errors)) {
            errorMessage = data.validation_errors.join('\n');
          }

          setVerificationMessage(errorMessage);
          setVerificationMessageType('error');
        }
      } catch (err) {
        logger.error('Verify account error:', err);
        setVerificationMessage('An error occurred during verification. Please try again.');
        setVerificationMessageType('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleResendOtp = async (e) => {
    e.preventDefault();
    setIsResending(true);
    setResendMessage('');
    setResendMessageType('');
    // Clear verification error message when resending
    setVerificationMessage('');
    setVerificationMessageType('');

    try {
      // Use different API endpoint for resend based on verification type
      const resendEndpoint = verificationType === 'forgot-password'
        ? config.API_ENDPOINTS.FORGOT_PASSWORD
        : config.API_ENDPOINTS.RESEND_VERIFICATION;

      const response = await fetch(`${config.API_BASE_URL}${resendEndpoint}`, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setResendMessage(data.message || 'New verification code sent successfully. Please check your email.');
        setResendMessageType('success');
        // Reset OTP fields
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        // Start 2-minute countdown
        setCountdown(120);
        // Save countdown to localStorage with timestamp
        localStorage.setItem(config.STORAGE_KEYS.RESEND_COUNTDOWN, '120');
        localStorage.setItem(config.STORAGE_KEYS.RESEND_TIMESTAMP, Date.now().toString());
      } else {
        // Handle error response
        let errorMessage = data.message || 'Failed to resend verification code. Please try again.';

        // Handle validation errors if they exist
        if (data.validation_errors && Array.isArray(data.validation_errors)) {
          errorMessage = data.validation_errors.join('\n');
        }

        setResendMessage(errorMessage);
        setResendMessageType('error');
      }
    } catch (err) {
      logger.error('Resend verification error:', err);
      setResendMessage('An error occurred while resending the verification code. Please try again.');
      setResendMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <main
      className={styles['verify-container']}
      id="verify-main"
      aria-label="Verify your account"
    >
      <div className={styles['verify-content']}>
        <div className={styles['logo-wrap']}>
          <BrandLogo alt="Richuel Logo" />
        </div>

        <div className={styles['verify-form-container']}>
          <h1 className={styles['verify-title']}>Verify Your Account</h1>

          <div className={styles['verify-instructions']}>
            <p>We've sent a 6-digit code to <span className={styles['email-highlight']}>{email}</span></p>
            <p>Please enter it below.</p>
          </div>

          {resendMessage && (
            <div
              key={resendMessage}
              id="verify-resend-message"
              className={`${styles['resend-message']} ${styles[resendMessageType]}`}
              role="status"
              aria-live="polite"
            >
              {resendMessage}
            </div>
          )}

          {verificationMessage && (
            <div
              key={verificationMessage}
              id="verify-message"
              className={`${styles['error-message']} ${verificationMessageType === 'success' ? styles['success-message'] : ''}`}
              role={verificationMessageType === 'success' ? 'status' : 'alert'}
              aria-live="polite"
            >
              {verificationMessage.split('\n').map((messageLine, index) => (
                <div key={index}>{messageLine}</div>
              ))}
            </div>
          )}

          {!isVerified ? (
            <>
              <form className={styles['verify-form']} onSubmit={handleSubmit} aria-label="Enter verification code">
                <div className={styles['form-group']} role="group" aria-labelledby="verify-otp-label">
                  <label id="verify-otp-label" htmlFor="verify-otp-0" className={styles['otp-label']}>
                    Enter OTP
                  </label>
                  <div className={styles['otp-inputs']}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        id={index === 0 ? 'verify-otp-0' : undefined}
                        aria-label={`Digit ${index + 1} of 6`}
                        aria-describedby={verificationMessage ? 'verify-message' : undefined}
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className={[styles['otp-input'], digit ? styles.filled : ''].filter(Boolean).join(' ')}
                        required
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className={styles['submit-button']}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  aria-label={isSubmitting ? 'Verifying…' : 'Submit verification code'}
                >
                  {isSubmitting ? 'Verifying...' : 'Submit'}
                </button>
              </form>

              <div className={styles['resend-otp']}>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending || isSubmitting || countdown > 0}
                  className={styles['resend-button']}
                  aria-label={
                    isResending
                      ? 'Sending new code…'
                      : countdown > 0
                        ? `Resend OTP available in ${formatTime(countdown)}`
                        : 'Resend OTP'
                  }
                >
                  {isResending ? 'Sending...' : countdown > 0 ? `Resend OTP (${formatTime(countdown)})` : 'Resend OTP'}
                </button>
              </div>
            </>
          ) : (
            <div key="verify-success" className={styles['success-container']} role="status" aria-live="polite">
              {verificationType === 'forgot-password' ? (
                <div key="verify-redirect" className={styles['redirect-message']}>
                  <p>Redirecting to reset password page...</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleLoginClick}
                  className={styles['login-button']}
                  aria-label="Go to login"
                >
                  Go to Login
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Verify; 